import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { CATEGORIES_UI } from "@/home/content";
import { fetchSettings } from "@/lib/api";

const DEFAULT_SETTINGS = {
  company_name: "Hema Connoisseur Collections (P) Limited",
  address_line1: "C-58/3 Okhla Industrial Area Phase-2",
  address_line2: "New Delhi 110020, India",
  phone: "+91.11.41032500",
  email_primary: "info@hema.in",
  email_secondary: "amit@hema.in",
  footer_statement:
    "Hema Connoisseur Collections connects distinguished beverage houses with India's hospitality, retail, diplomatic and institutional markets.",
};

const COL_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Alcoholic Portfolio", to: "/portfolio" },
  { label: "Distribution", to: "/distribution" },
  { label: "Infrastructure", to: "/infrastructure" },
];

const COMPANY_LINKS = [
  { label: "Partner With Us", to: "/partner" },
  { label: "Contact", to: "/contact" },
  { label: "Non-Alcoholic & FMCG", to: "/non-alcoholic-fmcg" },
];

const LEGAL_LINKS = [
  { label: "Terms of Use", to: "/legal/terms" },
  { label: "Privacy Policy", to: "/legal/privacy" },
  { label: "Cookie Policy", to: "/legal/cookies" },
  { label: "Responsible Communication", to: "/legal/responsible-communication" },
];

const colTitle = "text-[10px] uppercase tracking-[0.3em] text-[#C7A45D] mb-5";
const colLink = "block py-1.5 text-[13px] font-light text-[#F7F1E7]/65 hover:text-[#F7F1E7] transition-colors duration-300";

export const SiteFooter = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  useEffect(() => {
    fetchSettings()
      .then((s) =>
        setSettings((d) => ({ ...d, ...Object.fromEntries(Object.entries(s).filter(([, v]) => v)) }))
      )
      .catch(() => {});
  }, []);

  return (
  <footer data-testid="site-footer" className="relative z-10 bg-[#111111] text-[#F7F1E7] border-t border-white/10">
    <div className="px-6 sm:px-10 xl:px-16 py-16 sm:py-20 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-12">
      <div className="xl:col-span-2 max-w-sm">
        <Logo width={110} onDark />
        <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-[#F7F1E7]/85 font-medium">
          {settings.company_name}
        </p>
        <p className="mt-4 text-sm font-light leading-relaxed text-[#F7F1E7]/60">
          {settings.footer_statement}
        </p>
        <p className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#C7A45D]/80">Est. 2001 · Delhi NCR, India</p>
      </div>

      <div>
        <p className={colTitle}>Navigate</p>
        {COL_LINKS.map((l) => (
          <Link key={l.label} to={l.to} className={colLink}>
            {l.label}
          </Link>
        ))}
      </div>

      <div>
        <p className={colTitle}>Portfolio</p>
        {CATEGORIES_UI.map((c) => (
          <Link key={c.slug} to={`/portfolio?category=${c.slug}`} className={colLink} data-testid={`footer-cat-${c.slug}`}>
            {c.name}
          </Link>
        ))}
      </div>

      <div>
        <p className={colTitle}>Company</p>
        {COMPANY_LINKS.map((l) => (
          <Link key={l.label} to={l.to} className={colLink} data-testid={`footer-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {l.label}
          </Link>
        ))}
        {settings.linkedin ? (
          <a href={settings.linkedin} target="_blank" rel="noreferrer" className={colLink}>
            LinkedIn
          </a>
        ) : (
          <span className={`${colLink} cursor-default text-[#F7F1E7]/35`} aria-disabled="true">
            LinkedIn — coming soon
          </span>
        )}
        <div className="mt-5 text-[12px] font-light text-[#F7F1E7]/55 leading-relaxed space-y-1" data-testid="footer-contact-block">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#C7A45D]/80 mb-2">Registered Office</p>
          <p>{settings.address_line1}</p>
          <p>{settings.address_line2}</p>
          <p className="pt-1.5">
            <a href={`tel:${settings.phone.replace(/[^+\d]/g, "")}`} className="hover:text-[#F7F1E7] transition-colors" data-testid="footer-phone">
              {settings.phone}
            </a>
          </p>
          <p>
            <a href={`mailto:${settings.email_primary}`} className="hover:text-[#F7F1E7] transition-colors" data-testid="footer-email-primary">
              {settings.email_primary}
            </a>
          </p>
          {settings.email_secondary && (
            <p>
              <a href={`mailto:${settings.email_secondary}`} className="hover:text-[#F7F1E7] transition-colors" data-testid="footer-email-secondary">
                {settings.email_secondary}
              </a>
            </p>
          )}
        </div>
      </div>
    </div>

    <div className="px-6 sm:px-10 xl:px-16 py-8 border-t border-white/10">
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {LEGAL_LINKS.map((l) => (
          <Link key={l.label} to={l.to} className="text-[11px] tracking-[0.1em] text-[#F7F1E7]/50 hover:text-[#C7A45D] transition-colors" data-testid={`footer-legal-${l.to.split("/").pop()}`}>
            {l.label}
          </Link>
        ))}
        <span className="text-[11px] tracking-[0.1em] text-[#F7F1E7]/50">
          This website is intended for visitors of legal drinking age in their jurisdiction.
        </span>
      </div>
      <p className="mt-5 max-w-3xl text-[11px] font-light leading-relaxed text-[#F7F1E7]/40">
        This website is intended for corporate, trade and portfolio information. Alcoholic beverage
        communication is presented responsibly and is not intended to encourage excessive or underage
        consumption.
      </p>
      <p className="mt-4 text-[11px] tracking-[0.12em] text-[#F7F1E7]/40" data-testid="footer-copyright">
        © {new Date().getFullYear()} HEMA CONNOISSEUR COLLECTIONS (P) LIMITED. All rights reserved.
      </p>
    </div>
  </footer>
  );
};
